// BHP-1160: one document of an invoice, in whichever of its three states it is.
//
// The states are kept together in one component because they are one decision:
// a panel that renders the viewer only when a document is `available` cannot
// accidentally ask for bytes that are not there, and the two non-available
// states then have exactly one place to be worded.
//
// Neither message names Exact Online, Hetzner or any other supplier, and the
// technical failure reason never leaves the backend (BHP-1144). "Preparing" and
// "we could not prepare it" is the whole truth a customer can act on.
import { type DocumentState } from '../invoice';
import { type ApiFile } from '../../../shared/http/httpClient';
import { ApiError } from '../../../shared/http/ApiError';
import { toUserMessage } from '../../../shared/http/errorMessage';
import PdfViewer from './PdfViewer';

interface DocumentPanelProps {
  state: DocumentState;
  document: ApiFile | undefined;
  isPending: boolean;
  error: unknown;
  /** Used only when the server sent no filename of its own. */
  fileName: string;
  title: string;
}

function Notice({
  tone,
  children
}: {
  tone: 'neutral' | 'warning';
  children: React.ReactNode;
}): JSX.Element {
  const toneClasses =
    tone === 'warning'
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : 'border-slate-200 bg-slate-50 text-slate-600';

  return (
    <div
      className={`rounded-lg border border-solid p-4 text-sm ${toneClasses}`}
      role={tone === 'warning' ? 'alert' : undefined}
    >
      {children}
    </div>
  );
}

export default function DocumentPanel({
  state,
  document,
  isPending,
  error,
  fileName,
  title
}: DocumentPanelProps): JSX.Element {
  if (state === 'preparing') {
    return (
      <Notice tone="neutral">
        This document is being prepared and will appear here shortly.
      </Notice>
    );
  }

  if (state === 'unavailable') {
    return (
      <Notice tone="warning">
        We could not prepare this document. Please contact us and we will sort
        it out for you.
      </Notice>
    );
  }

  // A document we already hold wins over an error. React Query keeps `data`
  // when a *background* refetch fails — switching the active customer
  // invalidates these queries — and replacing a readable invoice with an error
  // banner because a refresh failed is worse than showing the copy we have.
  if (document !== undefined) {
    return (
      <PdfViewer
        document={document.blob}
        fileName={document.fileName ?? fileName}
        title={title}
      />
    );
  }

  if (isPending) {
    return <p className="text-sm text-slate-500">Loading document…</p>;
  }

  // The invoice said the document was there, and fetching it still failed. That
  // is not the customer's problem to diagnose, so it reads like the state above
  // rather than like a stack trace.
  return <Notice tone="warning">{failureMessage(error)}</Notice>;
}

/**
 * An expired session is not a support case. A 401/403 means "sign in again",
 * which the app handles elsewhere; telling that customer to contact us sends
 * them down a road with nothing at the end of it.
 */
function failureMessage(error: unknown): string {
  const status = error instanceof ApiError ? error.status : 0;

  if (status === 401 || status === 403) {
    return 'Your session has expired. Please sign in again to view this document.';
  }

  if (status >= 400 && status < 500) {
    return 'This document is no longer available. Please contact us and we will sort it out for you.';
  }

  return toUserMessage(error, 'The document could not be loaded.');
}
